import React, { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface MasterItem {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
}

interface MasterManagementProps {
  title: string;
  itemLabel: string;
  loadItems: () => Promise<MasterItem[]>;
  createItem: (data: any) => Promise<any>;
  updateItem: (id: string, data: any) => Promise<any>;
  deleteItem: (id: string) => Promise<any>;
}

const MasterManagement: React.FC<MasterManagementProps> = ({
  title,
  itemLabel,
  loadItems,
  createItem,
  updateItem,
  deleteItem,
}) => {
  const [list, setList] = useState<MasterItem[]>([]);
  const [form, setForm] = useState({ name: '', slug: '', description: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await loadItems();
      setList(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setList([]);
      setError(err.response?.data?.message || `Failed to load ${title.toLowerCase()}`);
    }
  }, [loadItems, title]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({ name: '', slug: '', description: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await updateItem(editingId, form);
      } else {
        await createItem(form);
      }
      resetForm();
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to save ${itemLabel.toLowerCase()}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete this ${itemLabel.toLowerCase()}?`)) return;
    try {
      setError('');
      await deleteItem(id);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to delete ${itemLabel.toLowerCase()}`);
    }
  };

  return (
    <>
      <Header title={title} />
      <div className="page-container">
        <div className="page-header">
          <h1>{title}</h1>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setForm({ name: '', slug: '', description: '' });
            }}
            className="btn-primary"
          >
            {showForm ? 'Cancel' : `+ Add New ${itemLabel}`}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showForm && (
          <div className="form-container">
            <h2>{editingId ? `Edit ${itemLabel}` : `Create New ${itemLabel}`}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    placeholder={`e.g., ${itemLabel === 'Class' ? 'XII' : 'Mathematics'}`}
                  />
                </div>

                <div className="form-group">
                  <label>Slug</label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="Optional slug"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingId ? `Update ${itemLabel}` : `Create ${itemLabel}`}
                </button>
                <button type="button" onClick={resetForm} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item._id}>
                  <td>{item.name}</td>
                  <td>{item.slug}</td>
                  <td>{item.description}</td>
                  <td className="action-cell">
                    <button
                      onClick={() => {
                        setEditingId(item._id);
                        setForm({
                          name: item.name,
                          slug: item.slug || '',
                          description: item.description || '',
                        });
                        setShowForm(true);
                      }}
                      className="btn-small btn-edit"
                    >
                      Edit
                    </button>
                    <button onClick={() => handleDelete(item._id)} className="btn-small btn-delete">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MasterManagement;
